<template>
    <div class="relative">
        <div v-if="edit">
            <EmployeeEmploymentFields
                v-model:employee-tabel-code="employeeTabelCode"
                v-model:department="department"
                v-model:position="position"
                v-model:hired-date="hiredDate"
                v-model:fired-date="firedDate"
                v-model:percentage="percentage"
                v-model:shift-schedule="shiftSchedule"
                :departments="departmentStore.myDepartments"
                :positions="scheduleStore.positions"
                :shift-schedules="scheduleStore.shiftSchedules"
                :employee-tabel-code-readonly="hasReadonlyEmployeeTabelCode"
                @change="validate"
            />
        </div>
        <div v-else class="ds-employee-form-data-grid">
            <div class="ds-employee-form-data-column">
                <EmployeeDataItem label="Табельный код" :value="employeeTabelCodeLabel" v-if="employeeTabelCodeLabel" />
                <EmployeeDataItem label="Подразделение" :value="departmentLabel" v-if="departmentLabel" />
                <EmployeeDataItem label="Должность" :value="positionLabel" v-if="positionLabel" />
                <EmployeeDataItem label="Дата начала" :value="hired" />
            </div>
            <div class="ds-employee-form-data-column">
                <EmployeeDataItem label="Дата окончания" :value="fired" />
                <EmployeeDataItem label="Доля рабочих часов" :value="percent" />
                <EmployeeDataItem label="Доступность" :value="availability" />
            </div>
        </div>
        <div class="pt-3 text-right" v-if="edit">
            <btn @click="save" :disabled="isDisabled" :class="{ 'opacity-50': isDisabled }">Сохранить</btn>
            <btn class="ml-3" type="secondary" @click="cancel">Отмена</btn>
        </div>
        <div v-if="!edit && isEditable && !props.archive" class="absolute right-0 top-0 text-xs cursor-pointer text-graylight" @click="change">
            Изменить
        </div>
    </div>
</template>

<script setup lang="ts">
import EmployeeDataItem from './EmployeeDataItem.vue'
import EmployeeEmploymentFields from './EmployeeEmploymentFields.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useEmploymentStore } from '@/stores/employmentStore'
import { useAuthStore } from '@/stores/authStore'
import { computed, ref, watch } from 'vue'
import type { Employment, Option } from '@/types'
import dayjs from 'dayjs'
import { formatEmploymentDateForApi } from '@/utils/employmentDate'

const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const employmentStore = useEmploymentStore()
const authStore = useAuthStore()
const props = defineProps<{
    employment: Employment
    archive: boolean
}>()
const emit = defineEmits(['fired'])

const employment = computed(() => props.employment)

const isEditable = computed(() => !!departmentStore.myDepartments.filter(item => item.id === employment.value.department).length && authStore.permissions?.can_edit_employment)
const isDisabled = ref(true)
const edit = ref(false)
const employeeTabelCode = ref('')
const position = ref<Option | null>(null)
const department = ref<Option | null>(null)
const hiredDate = ref()
const firedDate = ref()
const percentage = ref('100')
const shiftSchedule = ref<Option | null>(null)

const findOption = (options: Option[], id?: string | null) => {
    if (!id) return null
    return options.find(item => item.id === id) || null
}

const departmentLabel = computed(() => findOption(departmentStore.departments, employment.value.department)?.name || '')
const positionLabel = computed(() => findOption(scheduleStore.positions, employment.value.position)?.name || '')
const employeeTabelCodeLabel = computed(() => employment.value.employee_tabel_code || '')
const hasReadonlyEmployeeTabelCode = computed(() => Boolean(employment.value.employee_tabel_code))
const hired = computed(() => employment.value.hired_at ? dayjs(employment.value.hired_at).format('DD.MM.YYYY') : '-')
const fired = computed(() => employment.value.fired_at ? dayjs(employment.value.fired_at).format('DD.MM.YYYY') : '-')
const availability = computed(() => findOption(scheduleStore.shiftSchedules, employment.value.shift_schedule)?.name || '')
const originalPercentage = computed(() => {
    const rawPercentage = Number(employment.value.percentage)
    return Number.isFinite(rawPercentage) ? rawPercentage : 1
})
const percent = computed(() => `${Math.round(originalPercentage.value * 100)}%`)

const syncFromEmployment = () => {
    employeeTabelCode.value = employment.value.employee_tabel_code || ''
    department.value = findOption(departmentStore.myDepartments, employment.value.department)
        || findOption(departmentStore.departments, employment.value.department)
    position.value = findOption(scheduleStore.positions, employment.value.position)
    hiredDate.value = employment.value.hired_at ? dayjs(employment.value.hired_at) : undefined
    firedDate.value = employment.value.fired_at ? dayjs(employment.value.fired_at) : undefined
    percentage.value = String(Math.round(originalPercentage.value * 100))
    shiftSchedule.value = findOption(scheduleStore.shiftSchedules, employment.value.shift_schedule)
}

syncFromEmployment()

const change = () => {
    syncFromEmployment()
    validate()
    edit.value = true
}

const cancel = async () => {
    syncFromEmployment()
    edit.value = false
}

const validate = () => {
    const hasValidPercentage = /^\d+$/.test(percentage.value) && Number(percentage.value) >= 0 && Number(percentage.value) <= 100
    const hasValidDateRange = Boolean(hiredDate.value) && (!firedDate.value || dayjs(hiredDate.value).isBefore(dayjs(firedDate.value)))
    const hiredAt = formatEmploymentDateForApi(hiredDate.value)
    const firedAt = formatEmploymentDateForApi(firedDate.value)
    const hasChanges = (
        (!hasReadonlyEmployeeTabelCode.value && Boolean(employeeTabelCode.value) && employeeTabelCode.value !== employment.value.employee_tabel_code) ||
        department.value?.id !== employment.value.department ||
        position.value?.id !== employment.value.position ||
        hiredAt !== formatEmploymentDateForApi(employment.value.hired_at) ||
        firedAt !== formatEmploymentDateForApi(employment.value.fired_at) ||
        Number(percentage.value) / 100 !== originalPercentage.value ||
        shiftSchedule.value?.id !== employment.value.shift_schedule
    )

    isDisabled.value = !(
        hasChanges &&
        department.value?.id &&
        position.value?.id &&
        shiftSchedule.value?.id &&
        hasValidPercentage &&
        hasValidDateRange
    )
}

const save = async () => {
    const hiredAt = formatEmploymentDateForApi(hiredDate.value)
    const firedAt = formatEmploymentDateForApi(firedDate.value)
    const data: Record<string, string | number | null | undefined> = {}

    if(!hasReadonlyEmployeeTabelCode.value && employeeTabelCode.value && employeeTabelCode.value !== employment.value.employee_tabel_code) data.employee_tabel_code = employeeTabelCode.value
    if(department.value?.id !== employment.value.department) data.department = department.value?.id
    if(position.value?.id !== employment.value.position) data.position = position.value?.id
    if(hiredAt !== formatEmploymentDateForApi(employment.value.hired_at)) data.hired_at = hiredAt
    if(firedAt !== formatEmploymentDateForApi(employment.value.fired_at)) data.fired_at = firedAt
    if(Number(percentage.value) / 100 !== originalPercentage.value) data.percentage = Number(percentage.value) / 100
    if(shiftSchedule.value?.id !== employment.value.shift_schedule) data.shift_schedule = shiftSchedule.value?.id

    edit.value = false
    await employmentStore.changeEmployment(employment.value.id, data)
    emit('fired')
}

watch(
    () => props.employment,
    () => {
        if (!edit.value) syncFromEmployment()
    }
)

watch([hiredDate, firedDate, percentage, position, department, shiftSchedule, employeeTabelCode], () => validate())
</script>
